import type { StampInSheetData, UserSheetData, UserSheetHistoryData } from '@/schemas/v2'

/** ポイントカード 1 枚あたりの枠数（3 列 × 5 行） */
export const POINT_CARD_SLOTS_PER_PAGE = 15

/** ポイントカードの 1 枠。未獲得枠は acquired: false のみを持つ */
export type PointCardSlot =
  | { acquired: false }
  | {
      acquired: true
      /** user_point_transactions.id（React key 用に一意） */
      transactionId: number
      stampId: number
      name: string
      image: string | null
      point: number
      /** 獲得日時（ISO8601）。未設定データは null */
      acquiredAt: string | null
      spots: StampInSheetData['spots']
    }

/** createdAt をソート用の数値に変換する。null / パース不能な文字列は 0（最古扱い） */
function parseCreatedAt(createdAt: string | null): number {
  if (!createdAt) return 0
  const t = Date.parse(createdAt)
  return Number.isNaN(t) ? 0 : t
}

/**
 * 獲得履歴を「古い順」に 15 枠ずつのページへ詰める。
 *
 * - API の history は createdAt DESC（新しい順）なので昇順へ反転してから詰める
 *   （＝ 1 枚目の 1 枠目が最初の獲得スタンプになる）
 * - 獲得数が 0 でも空カードを 1 枚返す
 * - 獲得数がちょうど 15 の倍数のときは、次の空カードを 1 枚追加する（#2970 要件）
 *
 * @param history GET /api/v2/user/sheets の history（1 要素 = 1 獲得）
 * @param stamps  GET /api/v2/sheets のマスタスタンプ（spots / point の解決に使用）
 */
export function buildPointCardPages(history: UserSheetHistoryData[], stamps: StampInSheetData[]): PointCardSlot[][] {
  const stampMap = new Map(stamps.map((s) => [s.id, s]))

  // createdAt 昇順（古い順）。createdAt が同値/null/パース不能の場合は id の昇順で安定させる
  const ascending = [...history].sort((a, b) => {
    const ta = parseCreatedAt(a.createdAt)
    const tb = parseCreatedAt(b.createdAt)
    return ta !== tb ? ta - tb : a.id - b.id
  })

  const filled: PointCardSlot[] = ascending.map((h) => {
    const master = stampMap.get(h.stampId)
    return {
      acquired: true,
      transactionId: h.id,
      stampId: h.stampId,
      name: h.name || (master?.name ?? ''),
      image: h.image ?? master?.image ?? null,
      point: h.point ?? master?.point ?? 0,
      acquiredAt: h.createdAt,
      spots: master?.spots ?? [],
    }
  })

  // 「ちょうど 15 個」のときに次の空カードを 1 枚増やすため floor + 1 で数える
  const pageCount = Math.floor(filled.length / POINT_CARD_SLOTS_PER_PAGE) + 1

  return Array.from({ length: pageCount }, (_, page) => {
    const offset = page * POINT_CARD_SLOTS_PER_PAGE
    return Array.from(
      { length: POINT_CARD_SLOTS_PER_PAGE },
      (_, i): PointCardSlot => filled[offset + i] ?? { acquired: false },
    )
  })
}

/**
 * 今回の獲得のうち、point_card シートに入った個数をシート別に数える（#2970 充填通知用）。
 *
 * スタンプとシートは多対多で、GET /api/v2/user/sheets は 1 回の獲得をそのスタンプを含む全シートで数える。
 * そのため 1 獲得を所属する全 point_card シートにそれぞれ加算する。
 *
 * @param acquiredStampIds 今回の獲得フローで得たスタンプ ID（null はスタンプ ID 不明のため数えない）
 * @param pointCardSheetIdsByStampId stampId → そのスタンプを含む point_card シート ID 一覧のマップ
 */
export function countPointCardAcquisitionsBySheet(
  acquiredStampIds: (number | null)[],
  pointCardSheetIdsByStampId: ReadonlyMap<number, readonly number[]>,
): Map<number, number> {
  const deltaBySheet = new Map<number, number>()
  for (const stampId of acquiredStampIds) {
    if (stampId === null) continue
    for (const sheetId of pointCardSheetIdsByStampId.get(stampId) ?? []) {
      deltaBySheet.set(sheetId, (deltaBySheet.get(sheetId) ?? 0) + 1)
    }
  }
  return deltaBySheet
}

/**
 * 今回の獲得でいずれかの point_card シートのカードが 1 枚以上埋まったかを判定する（#2970 充填通知用）。
 *
 * 「獲得前の累計」はキャッシュに頼らず、獲得後の累計（サーバー最新値）から今回の獲得数を引いて逆算する。
 * 「カードが埋まった」= 累計を 15 で割った商が増えたこと（1 回の獲得で複数枚跨ぐケースも含む）。
 * 累計（meta.totalStampCount）が取れないシートは false とする。
 *
 * @param deltaBySheet {@link countPointCardAcquisitionsBySheet} の結果
 * @param sheetsAfter GET /api/v2/user/sheets の獲得後のシート一覧
 */
export function hasFilledPointCard(
  deltaBySheet: ReadonlyMap<number, number>,
  sheetsAfter: Pick<UserSheetData, 'id' | 'meta'>[],
): boolean {
  return sheetsAfter.some((sheet) => {
    const delta = deltaBySheet.get(sheet.id)
    if (!delta) return false
    const after = sheet.meta?.totalStampCount
    // 累計が取れないシートは判定しない（0 扱いにすると before が負になり商が増えたと誤判定する）
    if (after === null || after === undefined) return false
    // サーバー値と今回の獲得数が食い違っても負にならないよう 0 で下限を取る
    const before = Math.max(0, after - delta)
    return Math.floor(after / POINT_CARD_SLOTS_PER_PAGE) > Math.floor(before / POINT_CARD_SLOTS_PER_PAGE)
  })
}
